// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportAlert } from '@/components/support-alert';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { dismissTriageCustomer } from '@/customer/api/triage-dismiss-customer';
import { CustomerTriage } from '@/customer/api/triage-types';
import { TriageInfoRow } from '@/customer/components/triage-info-row';
import { customerTriageQueryKey } from '@/customer/triage-query-keys';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface CustomerTriageDismissDialogProps {
  isOpen: boolean;
  triage: CustomerTriage | null;
  onClose: () => void;
}

export function CustomerTriageDismissDialog(
  props: CustomerTriageDismissDialogProps
) {
  const { isOpen, triage, onClose } = props;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const dismissMutation = useMutation({
    mutationFn: () => {
      if (!triage) {
        throw new Error('No triage entry selected');
      }
      return dismissTriageCustomer(triage.key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerTriageQueryKey });
      setErrorMessage('');
      onClose();

      showToast({
        severity: 'success',
        element: (
          <span>Customer &quot;{triage?.displayName}&quot; dismissed</span>
        ),
      });
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
      setErrorMessage(curated);
    },
  });

  const handleConfirm = () => {
    setErrorMessage('');
    dismissMutation.mutate();
  };

  const handleClose = () => {
    if (dismissMutation.isPending) {
      return;
    }
    setErrorMessage('');
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (dismissMutation.isPending) {
        return;
      }
    }
    handleClose();
  };

  if (!triage) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dismiss-customer-triage-dialog-title"
    >
      <DialogTitle
        id="dismiss-customer-triage-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Dismiss customer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Remove this customer from the review queue
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={dismissMutation.isPending}
          aria-label="Close"
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <TriageInfoRow label="Display name" value={triage.displayName} />
            <TriageInfoRow label="Company" value={triage.companyName} />
            <TriageInfoRow label="Domain" value={triage.domain} />
            <TriageInfoRow label="Email" value={triage.email} />
          </Box>

          <Typography variant="body2" color="text.secondary">
            This customer will be moved to the &quot;dismissed&quot; tab. You
            can classify it later if needed.
          </Typography>

          {errorMessage &&
            (errorMessage === SUPPORT_FALLBACK_ERROR_MESSAGE ? (
              <SupportAlert
                message={errorMessage}
                onClose={() => setErrorMessage('')}
              />
            ) : (
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            ))}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          size="small"
          variant="text"
          onClick={handleClose}
          disabled={dismissMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleConfirm}
          disabled={dismissMutation.isPending}
        >
          {dismissMutation.isPending ? 'Dismissing...' : 'Dismiss'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
